import { Component, OnInit, ViewEncapsulation } from '@angular/core';
import { BaseService, OfferingImageApiService } from '@core';
import { ProductTypeConfig, Program, ProgramKit, ProgramProductCategoryTag, Supplier } from '@models';
import { UtilFns } from '@utils';
import { ToastrService } from 'ngx-toastr';
import { MenuItem } from 'primeng/api';
import { DialogService, DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { firstValueFrom } from 'rxjs';
import { AccountDbQueryService } from '../services/account-db-query.service';
import { AccountDbSaveService } from '../services/account-db-save.service';
import * as _ from 'lodash';

export interface IProgramTestMenuParams {
  program: Program;
}

export interface IMenuInfo {
  supplierId?: string;
  pptt?: ProgramProductCategoryTag;
}

export interface ICategorySelection {
  supplierId?: string;
  programProductTypeTagId?: string;
}

@Component({
  selector: 'prox-program-test-menu-dialog',
  standalone: false,
  templateUrl: './program-test-menu-dialog.component.html',
  encapsulation: ViewEncapsulation.None,
  styles: [
    `
      prox-program-test-menu-dialog {
        .p-menu {
          background: none;
          border: none;
          padding: 0;
          font-size: 1rem;
        }

        .p-menu .p-menu-item {
          padding: 0;
          margin-left: 5px;
          margin-bottom: 2px;
        }

        .p-menu .p-menu-submenu-label {
          color: #000;
          padding: 0;
          margin-bottom: 2px;
          margin-top: 8px;
          background: none;
          font-style: bold;
          font-size: 1.0625rem;
        }
      }
    `,
  ],
})
export class ProgramTestMenuDialogComponent implements OnInit {
  suppliers: Supplier[] = [];
  supplierId!: string;
  items: MenuItem[] = [];
  menuInfo: IMenuInfo[] = [];
  kits: ProgramKit[] = [];
  program!: Program;

  allProductTypeConfigs: ProductTypeConfig[] = [];
  filteredProductTypeConfigs: ProductTypeConfig[] = [];
  currentCategorySelection: ICategorySelection = {};
  /** Tile picture per ProductTypeConfig id (lower-cased); null shows the placeholder. */
  tileImageUrls = new Map<string, string | null>();

  constructor(
    public ref: DynamicDialogRef,
    private toastr: ToastrService,
    public config: DynamicDialogConfig<IProgramTestMenuParams>,
    private pngDialogService: DialogService,
    public dbQueryService: AccountDbQueryService,
    public dbSaveService: AccountDbSaveService,
    public baseService: BaseService,
    private offeringImageApi: OfferingImageApiService,
  ) {}

  async ngOnInit() {
    const params = this.config.data;
    UtilFns.assertNonNull(params);

    this.program = params.program;

    const programPtcs = await this.dbQueryService.getProgramProductTypeConfigsByProgram(this.program.id);
    this.allProductTypeConfigs = programPtcs.filter((x) => !x.isDiscontinued).map((x) => x.productTypeConfig);
    this.filteredProductTypeConfigs = this.allProductTypeConfigs.slice();

    // TODO: try not waiting for images - let them resolve lazily
    this.loadImages();

    this.suppliers = this.allProductTypeConfigs.map((x) => x.pricedProductType.productType.supplier);
    this.suppliers = _.uniq(this.suppliers);
    this.suppliers.unshift({ id: null, name: 'All' } as any as Supplier);
    this.onSupplierChange();
  }

  public static async openDialog(dialogService: DialogService, data?: IProgramTestMenuParams, config?: DynamicDialogConfig) {
    const ref = dialogService.open(ProgramTestMenuDialogComponent, {
      closable: true,
      draggable: true,
      width: '70vh',
      height: '90vh',
      maximizable: false,
      resizable: true,
      modal: true,
      // baseZIndex: 10000,
      focusOnShow: false,
      ...config,
      data: data,
    });
    const r = await firstValueFrom(ref.onClose);
    return r;
  }

  // Images come from the offering image service, as every image display now does (Jeff,
  // 2026-10-18): the supplier feed where one covers the style, else the image Proximity stores.
  // Asked by config, not by ProductType, so a config that sells only Light Blue shows Light Blue.
  private async loadImages() {
    const byConfig = await this.offeringImageApi.getForProductTypeConfigs(this.allProductTypeConfigs.map((x) => x.id));
    this.tileImageUrls = new Map([...byConfig].map(([id, colors]) => [id, OfferingImageApiService.tileImageUrl(colors)]));
  }

  // Null renders the placeholder.
  getListImageUrl(productTypeConfig: ProductTypeConfig): string | null {
    return this.tileImageUrls.get(productTypeConfig.id.toLowerCase()) ?? null;
  }

  onSupplierChange(evt?: any) {
    let menu: any[] = [];
    menu.push(this.kitItem());

    this.items = [
      {
        label: 'Product Categories',
        items: [
          {
            label: 'Show All',
            icon: 'fa-duotone fa-regular fa-tags',
            command: () => this.onSelectTag({ supplierId: this.supplierId }),
          },
        ],
      },
    ];

    let productTypeConfigs = this.allProductTypeConfigs.slice();
    if (this.supplierId != null) {
      productTypeConfigs = productTypeConfigs.filter((x) => x.pricedProductType.productType.supplierId == this.supplierId);
    }

    const programProductTypeTags = _.uniq(
      _.flatten(productTypeConfigs.map((x) => x.pricedProductType.programProductTypeTagMaps.map((y) => y.programProductTypeTag))),
    );

    const programProductCategoryTags = _.uniq(
      _.flatten(programProductTypeTags.map((ptt) => ptt.programProductCategoryTagMaps.map((x) => x.programProductCategoryTag))).filter(
        (x) => x.programId == this.program.id,
      ),
    );

    const programProductTypeTagIdSet = new Set(programProductTypeTags.map((x) => x.id));

    const moreItems = programProductCategoryTags.map((x) => {
      return {
        label: x.name,
        icon: 'fa-duotone fa-regular fa-tag',
        items: x.programProductCategoryTagMaps
          .filter((x) => programProductTypeTagIdSet.has(x.programProductTypeTagId))
          .map((y) => {
            const pptt = y.programProductTypeTag;
            return {
              label: pptt.name,
              icon: 'fa-duotone fa-solid fa-tag',
              command: () =>
                this.onSelectTag({
                  supplierId: this.supplierId,
                  programProductTypeTagId: pptt.id,
                }),
            };
          }),
      };
    });
    this.items.push(...moreItems);
    this.items.unshift(this.kitItem());

    //this.itemSelected.emit({ supplierId: this.supplierId });
  }

  // onSupplierChange(evt?: any) {
  //   let menu: any[] = [];
  //   menu.push(this.kitItem());

  //   this.items = [
  //     {
  //       label: 'Product Categories',
  //       items: [
  //         {
  //           label: 'Show All',
  //           icon: 'fa-duotone fa-regular fa-tags',
  //           command: () => this.itemSelected(this.supplierId),
  //         },
  //       ],
  //     },
  //   ];
  // }

  private kitItem(): MenuItem {
    if (this.program.programKits.length === 0) return [];
    return {
      label: 'Kits',
      items: this.program.programKits
        /*     .filter((x) =>
              programProductTypeTagIdSet.has(x.programProductTypeTagId)
            ) */
        .map((y) => {
          const kit = y.kit;
          return {
            label: kit.name,
            icon: 'fa-duotone fa-solid fa-box-taped',
            command: () => this.kitSelected(y),
          };
        }),
    };
  }

  onSelectTag(selection: ICategorySelection) {
    if (_.isEmpty(selection)) {
      selection = this.currentCategorySelection;
    } else {
      this.currentCategorySelection = selection;
    }
    this.filteredProductTypeConfigs = this.allProductTypeConfigs.slice();
    if (selection.supplierId != null) {
      this.filteredProductTypeConfigs = this.filteredProductTypeConfigs.filter(
        (x) => x.pricedProductType.productType.supplierId == selection.supplierId,
      );
    }

    if (selection.programProductTypeTagId != null) {
      this.filteredProductTypeConfigs = this.filteredProductTypeConfigs.filter((x) =>
        x.pricedProductType.programProductTypeTagMaps
          .map((x) => x.programProductTypeTagId)
          .includes(selection.programProductTypeTagId ?? ''),
      );
    }
  }

  kitSelected(programKit: ProgramKit) {}

  async onSelectProduct(row: ProductTypeConfig, evt: any) {
    // if (!(await this.canEditAsync())) return;
    // // if user click on cart icon, show item in cart, else show new item
    // const cartClicked = evt.target.className.includes('p-button');
    // const jod = cartClicked ? this.jobOrderDetails.find((x) => x.productTypeConfigProduct?.productTypeConfigId == row.id) : undefined;
    // const r = await JobOrderDetaiDialogComponent.openDialog(this.pngDialogService, {
    //   parent: this,
    //   jobOrderDetail: jod,
    //   jobOrder: this.jobOrder,
    //   productTypeConfigId: row.id,
    // });
    // this.forceJodRecalc();
  }
}
